import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Unlink } from 'lucide-react';
import { Button, Card, Pill, Select } from '../../../components/ui';
import { OUTSIDE_LABEL, plural } from '../format';
import type { GovNodeView, OutsideRecord } from '../types';

const VERDICT_TONE: Record<OutsideRecord['verdict'], 'amber' | 'neutral' | 'blue'> = {
  foreign_government: 'amber',
  not_public: 'neutral',
  public_unplaced: 'blue',
  obsolete: 'neutral',
};

/**
 * Records that sit in the directory but not in the state: another country's
 * embassy filed under this country's foreign ministry, a private bank under
 * the central bank, a public body nobody has placed yet.
 *
 * These are usually the oldest mistakes in a directory, and the map is the
 * first thing that can see them.
 */
export const OutsideList: React.FC<{
  records: OutsideRecord[];
  nodes: GovNodeView[];
  canEdit: boolean;
  busy?: boolean;
  onResolve: (organizationId: string, action: 'detach' | 'move' | 'keep', targetNodeId?: string | null) => void;
}> = ({ records, nodes, canEdit, busy, onResolve }) => {
  const [open, setOpen] = useState(false);
  const [targets, setTargets] = useState<Record<string, string>>({});

  if (!records.length) return null;

  const placeable = nodes.filter((node) => node.organizationId && node.status === 'applied');

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-neutral-50"
        aria-expanded={open}
      >
        <Unlink className="h-4 w-4 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] text-ink">Outside the structure</span>
          <span className="block text-[12px] text-neutral-400">
            {plural(records.length, 'record')} filed somewhere they do not belong
          </span>
        </span>
        <ChevronDown
          className={`h-4 w-4 flex-shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`}
          strokeWidth={1.75}
        />
      </button>

      {open && (
        <ul className="divide-y divide-neutral-100 border-t border-neutral-200/70">
          {records.map((record) => (
            <li key={record.organizationId} className="px-4 py-3">
              <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <Link
                      to={`/organizations/${record.organizationId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="truncate text-[13px] text-ink hover:underline hover:decoration-neutral-300 hover:underline-offset-2"
                    >
                      {record.label}
                    </Link>
                    <Pill tone={VERDICT_TONE[record.verdict]}>{OUTSIDE_LABEL[record.verdict]}</Pill>
                  </div>
                  <p className="mt-0.5 text-[12px] text-neutral-500">
                    {[
                      record.type,
                      record.parentLabel ? `filed under ${record.parentLabel}` : 'at the top level',
                      plural(record.people, 'person', 'people'),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  {record.reason && (
                    <p className="mt-1 text-[12px] text-neutral-400">{record.reason}</p>
                  )}
                </div>

                {canEdit && (
                  <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
                    {record.suggestion === 'move' && (
                      <Select
                        aria-label="Move under"
                        className="h-8 w-48 text-[12.5px]"
                        value={targets[record.organizationId] ?? record.targetNodeId ?? ''}
                        onChange={(event) =>
                          setTargets({ ...targets, [record.organizationId]: event.target.value })
                        }
                      >
                        <option value="">Choose where it belongs</option>
                        {placeable.map((node) => (
                          <option key={node.id} value={node.id}>
                            {node.name}
                          </option>
                        ))}
                      </Select>
                    )}
                    {record.suggestion === 'move' ? (
                      <Button
                        size="xs"
                        variant="secondary"
                        disabled={busy || !(targets[record.organizationId] ?? record.targetNodeId)}
                        onClick={() =>
                          onResolve(
                            record.organizationId,
                            'move',
                            targets[record.organizationId] ?? record.targetNodeId,
                          )
                        }
                      >
                        Move
                      </Button>
                    ) : record.parentLabel ? (
                      <Button
                        size="xs"
                        variant="secondary"
                        disabled={busy}
                        onClick={() => onResolve(record.organizationId, 'detach')}
                      >
                        Detach
                      </Button>
                    ) : null}
                    <Button
                      size="xs"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => onResolve(record.organizationId, 'keep')}
                    >
                      Leave it
                    </Button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};
